// 機体 (AutoFork)。サイト配下の robot に対応する。
export interface MachineInfo {
  id: string
  name: string
}

export interface SiteInfo {
  id: string
  name: string
  robots: MachineInfo[]
}

// プレイバックルート (1本の記録ルート)。機体へ接続して MQTT 経由で取得する。
// 機体の getMap レスポンスから記録ルート (rec-1/rec-2 スロットを持つ
// 部分マップ) を抽出したもの。シナリオを構成する最小単位。
export interface PlaybackRoute {
  // 一覧表示・選択用のキー。現状は mapId と同一。
  id: string
  // 記録ルートが属する部分マップの ID
  mapId: string
  // 開始/終了スロットの ID ([rec-1, rec-2])
  slots: string[]
  // buildRoutePoints の結果をキャッシュした中継点座標
  computedRoutePoints?: Array<{ x: number; y: number }> | null
  action?: ActionType
  isSwapped?: boolean
  startSlot?: string
  endSlot?: string
  // 停止手順（action === 'wait'）の停止秒数。停止手順は記録ルートではなく
  // mapId='' / slots=[停止位置スロット] / startSlot=endSlot=停止位置 で表し、
  // fork_webapp_react の実行時に機体の wait タスクへ変換される（routeUtil の createWaitStep）。
  // 一時停止手順（action === 'wait_resume'、タップで再開）も同じ形で秒数だけ持たない
  waitSec?: number
}

// ルートに搬送アクション・向きを付与した、シナリオ内の 1 手順。
// move_loaded は荷物を持ったままの移動（タスク送信時に --loaded が付与される）
// wait は停止手順（直前ルートの終点でその場に waitSec 秒停止する。走行しない）
// wait_resume は一時停止手順（直前ルートの終点でその場に停止し、タッチパネルの
// タップ（resume コマンド）で続きを実行する。秒数は持たない。走行しない）
export type ActionType =
  | 'move'
  | 'move_loaded'
  | 'pickup'
  | 'place'
  | 'wait'
  | 'wait_resume'

export interface RouteWithAction extends PlaybackRoute {
  action?: ActionType
  isSwapped?: boolean
  startSlot?: string
  endSlot?: string
  // 最適化済みのパス ([x, y] 配列)
  path?: [number, number][]
}

// シナリオ。複数ルートを順序付けてまとめた搬送手順。機体側に JSON 保存され、
// load_recorded_scenario で読み込む。
export interface PlaybackScenario {
  id: string
  routes: PlaybackRoute[]
  createdAt?: string // ISO 8601 形式の日時文字列
  title?: string // ユーザーが付けるシナリオ名（任意）
}

// シナリオ編集画面の表示モード（routeEdit はルート単体のマーク編集）
export type ViewMode = 'list' | 'create' | 'edit' | 'routeEdit'

// 連続するルート間の許容距離 (m)。これを超えると接続エラー。
export const DIST_THRESHOLD = 0.05

// ===============================
// 機体マップ (getMap レスポンス) の型
// fork_webapp_react の RouteMap を一覧表示に必要な範囲で移植したもの。
// ===============================

export interface MapCanvasParams {
  canvasWidthMeter: number
  imageOriginPixX: number
  imageOriginPixY: number
  imagePixelPerMeter: number
}
export interface Pose {
  x: number
  y: number
  theta: number
}

export interface Station {
  id: string
  pose: Pose
}

export interface Slot {
  id: string
  stationId: string
  height: number
  type: number
}

export interface Guideline {
  startId: string
  endId: string
  path?: [number, number][]
}

export interface RouteMap {
  stations: Map<string, Station>
  slots: Map<string, Slot>
  guidelines: Map<string, Guideline>
}

// ===============================
// ルート編集（マーク列の編集）の型
// ===============================

// 編集用マーク。trajectory_command の mark_at_poses 1 要素に対応する。
// 機体側はマークの theta を保存時に必ず経路方向（後進は +π）で上書きするため、
// 向きは自由角ではなく「前のマークからの区間を後進で走行するか」で持つ。
// theta は送信時に区間方向から算出する（computeMarkThetas）。
export interface EditableMark {
  x: number
  y: number
  // 前のマークからこのマークへの区間を後進で走行するか（先頭マークでは未使用）
  reverse: boolean
  // 荷役（高さ保存）マークか。機体側で永続化されるのは先頭・末尾マークのみ。
  isCargo: boolean
  // フォーク高 (m)。荷役でない場合は -1（荷役なし移動）
  height: number
  // マークが1点のみの間に指定した仮の向き。2点目配置時の出発方向あわせ
  // （alignEndpointToSlot）に使う。2点以上では区間方向が優先され無視される。
  draftTheta?: number
}
